import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Check, Circle } from 'lucide-react';
import { C } from '../theme';
import planiqLogo from '../assets/logo.png';

function StrengthChecklist({ password }) {
  const rules = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One special character', ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  return (
    <div style={{ marginTop: 8 }}>
      {rules.map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {r.ok
            ? <Check size={13} color="#2a8a5a" strokeWidth={2.5} />
            : <Circle size={13} color="#c0d0e0" strokeWidth={2} />}
          <span style={{ fontSize: 12, color: r.ok ? '#2a8a5a' : '#8aa0b8' }}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

const API = process.env.REACT_APP_API_URL || '';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8',
  borderRadius: 8, color: '#1a2a3a', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border .15s',
};

const labelStyle = {
  display: 'block', color: '#6a8aaa', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 6,
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function FocusInput({ type = 'text', ...props }) {
  return (
    <input
      type={type}
      {...props}
      style={inputStyle}
      onFocus={e => e.target.style.borderColor = '#0090d0'}
      onBlur={e => e.target.style.borderColor = '#c8d8e8'}
    />
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    org_name: '', org_type: 'agency',
    full_name: '', email: '',
    password: '', password_confirm: '',
    billing_email: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.password_confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        org_name: form.org_name,
        org_type: form.org_type,
        full_name: form.full_name,
        email: form.email,
        password: form.password,
      };
      if (form.billing_email.trim()) body.billing_email = form.billing_email.trim();
      const res = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const bg = {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 20,
    background: 'linear-gradient(135deg, #e8eef5 0%, #dce6f0 40%, #e4ecf5 70%, #d8e8f2 100%)',
    position: 'relative', overflow: 'hidden',
  };

  return (
    <div style={bg}>
      {/* Decorative shapes (same as LoginPage) */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', bottom: '8%', left: '8%', width: 120, height: 120, background: 'linear-gradient(135deg, #fff 60%, #c8d8e8)', borderRadius: 18, boxShadow: '8px 8px 32px rgba(100,140,180,0.18)', transform: 'rotate(-12deg)' }}/>
        <div style={{ position: 'absolute', bottom: '38%', left: '14%', width: 44, height: 44, background: 'linear-gradient(135deg, #e8f0f8, #d0e0ee)', borderRadius: 8, boxShadow: '4px 4px 16px rgba(100,140,180,0.15)', transform: 'rotate(-8deg)' }}/>
        <div style={{ position: 'absolute', bottom: '55%', left: '22%', width: 32, height: 32, background: 'linear-gradient(135deg, #f0f6fb, #dceaf5)', borderRadius: 6, boxShadow: '3px 3px 12px rgba(100,140,180,0.12)', transform: 'rotate(5deg)' }}/>
        <div style={{ position: 'absolute', bottom: '68%', left: '10%', width: 24, height: 24, background: 'linear-gradient(135deg, #e4eef8, #d4e4f0)', borderRadius: 5, transform: 'rotate(-15deg)' }}/>
        {[[85,20],[92,35],[78,45],[88,58],[95,70],[80,72],[90,82]].map(([l,t],i)=>(
          <div key={i} style={{ position: 'absolute', left: l+'%', top: t+'%', width: 6, height: 6, borderRadius: '50%', background: 'rgba(150,180,210,0.5)' }}/>
        ))}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.25 }}>
          <polyline points="85,20 92,35 78,45 88,58 95,70 80,72 90,82" vectorEffect="non-scaling-stroke" fill="none" stroke="#90b8d8" strokeWidth="1" strokeDasharray="4,4"/>
          <line x1="78" y1="45" x2="95" y2="70" vectorEffect="non-scaling-stroke" stroke="#90b8d8" strokeWidth="0.8" strokeDasharray="3,5"/>
          <line x1="85" y1="20" x2="78" y2="45" vectorEffect="non-scaling-stroke" stroke="#90b8d8" strokeWidth="0.8"/>
        </svg>
      </div>

      <div style={{ width: '100%', maxWidth: 460, position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ marginBottom: 8 }}>
            <img src={planiqLogo} alt="PlanIQ" style={{ width: 240, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: '#8aa0b8', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 8 }}>planiq.live</div>
          <div style={{ fontSize: 15, color: '#4a6a8a', fontWeight: 400 }}>Where digital planning meets execution.</div>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.9)',
          borderRadius: 16, padding: '36px 36px 28px',
          boxShadow: '0 8px 40px rgba(80,120,160,0.12)',
        }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
              <div style={{ marginBottom: 16, display:'flex', justifyContent:'center' }}><CheckCircle size={48} color='#2a8a5a' strokeWidth={1.5}/></div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1a2a3a', marginBottom: 10 }}>Registration submitted!</div>
              <div style={{ fontSize: 14, color: '#4a6a8a', lineHeight: 1.6, marginBottom: 24 }}>
                Your organisation <strong>{form.org_name}</strong> has been created and is awaiting approval.<br/>
                We'll notify you at <strong>{form.email}</strong> once your account is activated.
              </div>
              <button
                onClick={() => navigate('/login')}
                style={{
                  padding: '10px 28px',
                  background: 'linear-gradient(135deg, #0090d0, #0060a0)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0,120,200,0.25)',
                }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2a3a', marginBottom: 20 }}>Create your organisation</div>

              <Field label="Organisation name">
                <FocusInput value={form.org_name} onChange={set('org_name')} placeholder="Acme Media Agency" required autoFocus />
              </Field>

              <Field label="Organisation type">
                <select
                  value={form.org_type}
                  onChange={set('org_type')}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="agency">Agency</option>
                  <option value="advertiser">Advertiser</option>
                </select>
              </Field>

              <Field label="Your full name">
                <FocusInput value={form.full_name} onChange={set('full_name')} placeholder="Jane Smith" required />
              </Field>

              <Field label="Work email">
                <FocusInput type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" required />
              </Field>

              <Field label="Password">
                <FocusInput type="password" value={form.password} onChange={set('password')} placeholder="Min. 8 characters" required />
                {form.password && <StrengthChecklist password={form.password} />}
              </Field>

              <Field label="Confirm password">
                <FocusInput type="password" value={form.password_confirm} onChange={set('password_confirm')} placeholder="Repeat password" required />
              </Field>

              <Field label="Billing email (optional)">
                <FocusInput type="email" value={form.billing_email} onChange={set('billing_email')} placeholder="billing@company.com" />
              </Field>

              {error && (
                <div style={C.feedback('danger')}>{error}</div>
              )}

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '12px', marginTop: 4,
                background: loading ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
                border: 'none', borderRadius: 8, color: '#fff',
                fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 16px rgba(0,120,200,0.25)', transition: 'all .15s',
              }}>
                {loading ? 'Creating account…' : 'Create Account'}
              </button>

              <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6a8aaa' }}>
                Already have an account?{' '}
                <span
                  onClick={() => navigate('/login')}
                  style={{ color: '#0090d0', fontWeight: 600, cursor: 'pointer' }}
                >
                  Sign in
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
